<template>
  <div class="chat-workspace">
    <div class="history-sidebar" :class="{ 'is-collapsed': !isHistoryOpen }">
      <div class="history-sidebar-inner">
        <div class="sidebar-top">
          <div class="toggle-btn" @click="isHistoryOpen = !isHistoryOpen">
            <el-icon><Fold /></el-icon>
          </div>
          <div class="ai-profile">
            <div class="avatar">🤖</div>
            <div class="name">我的AI助理</div>
            <div class="tag">
              <el-icon><Cloudy /></el-icon> 云端
            </div>
          </div>
          <button class="new-chat-btn" @click="openNewChatDialog">
            <el-icon><Plus /></el-icon> 新对话
          </button>
        </div>

        <div class="history-list-container">
          <div class="list-title">最近对话</div>
          <div class="history-list">
            <div
              v-for="chat in chatHistoryList"
              :key="chat.id"
              class="history-item"
              :class="{ active: currentChatId === chat.id }"
              @click="selectChat(chat.id)"
            >
              <span class="text-truncate">{{ chat.title }}</span>
              <div class="action-icons">
                <el-icon
                  class="edit-icon"
                  @click.stop="openRenameDialog(chat.id, chat.title)"
                >
                  <EditPen />
                </el-icon>
                <el-icon
                  class="delete-icon"
                  @click.stop="confirmDeleteChat(chat.id)"
                >
                  <Delete />
                </el-icon>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="expand-btn-container" v-show="!isHistoryOpen">
      <el-button
        circle
        size="small"
        @click="isHistoryOpen = true"
        class="expand-btn"
      >
        <el-icon><Expand /></el-icon>
      </el-button>
    </div>

    <div class="chat-main-area">
      <div class="main-layout">
        <div class="chat-section" :class="{ 'is-shifted': showAndroid }">
          <el-card class="box-card" shadow="hover">
            <template #header>
              <div class="card-header">
                <span class="title"
                  >🚀 {{ currentChat?.title || "OpenClaw 指令控制台" }}</span
                >
                <div class="header-actions">
                  <el-tag
                    :type="
                      seaTokens > 500
                        ? 'success'
                        : seaTokens > 0
                        ? 'warning'
                        : 'danger'
                    "
                    size="small"
                    effect="plain"
                    style="margin-right: 8px"
                  >
                    🪙 {{ seaTokens }} ST
                  </el-tag>
                  <el-button
                    :type="showAndroid ? 'danger' : 'primary'"
                    size="small"
                    plain
                    @click="toggleAndroid"
                  >
                    <el-icon class="el-icon--left">
                      <Monitor v-if="!showAndroid" />
                      <Close v-else />
                    </el-icon>
                    {{ showAndroid ? "关闭云主机" : "打开云主机" }}
                  </el-button>
                </div>
              </div>
            </template>

            <div class="message-window" ref="messageWindow">
              <el-scrollbar ref="scrollbar">
                <div class="message-list">
                  <div
                    v-for="msg in currentChat?.messages || []"
                    :key="msg.id"
                    :class="[
                      'message-wrapper',
                      msg.role === 'user' ? 'is-user' : 'is-ai',
                    ]"
                  >
                    <div class="avatar">
                      {{ msg.role === "user" ? "🧑‍💻" : "🤖" }}
                    </div>
                    <div
                      class="message-bubble"
                      :class="{
                        'loading-bubble': msg.role === 'ai' && !msg.content,
                      }"
                    >
                      <template v-if="msg.role === 'ai' && !msg.content">
                        <span class="dot">.</span><span class="dot">.</span
                        ><span class="dot">.</span>
                      </template>
                      <template v-else>
                        <div class="content" style="white-space: pre-wrap">
                          {{ msg.content }}
                        </div>
                        <div class="time">{{ msg.time }}</div>
                      </template>
                    </div>
                  </div>
                </div>
              </el-scrollbar>
            </div>

            <div class="input-area">
              <el-input
                v-model="inputText"
                type="textarea"
                :rows="3"
                :placeholder="
                  seaTokens <= 0
                    ? 'SeaToken 已耗尽，无法继续对话'
                    : '输入指令或想对 Superman 说的话... (Enter 发送)'
                "
                @keydown.enter="handleEnter"
                resize="none"
                :disabled="!currentChatId || seaTokens <= 0"
              />
              <div class="action-bar">
                <el-button
                  type="info"
                  plain
                  @click="clearMessages"
                  :disabled="!currentChatId"
                  >清屏</el-button
                >
                <el-button
                  type="primary"
                  :loading="isWaiting"
                  @click="sendMessage"
                  :disabled="!currentChatId || seaTokens <= 0"
                >
                  发送指令
                  <el-icon class="el-icon--right"><Position /></el-icon>
                </el-button>
              </div>
            </div>
          </el-card>
        </div>

        <transition name="slide-fade">
          <div class="android-section" v-show="showAndroid">
            <el-card class="android-card" shadow="hover">
              <template #header>
                <div class="android-header">
                  <span class="title">💻 云主机画面</span>
                  <el-tag type="warning" size="small" effect="plain"
                    >Live</el-tag
                  >
                </div>
              </template>
              <div class="android-screen-container">
                <iframe
                  :src="vncUrl"
                  class="android-iframe"
                  frameborder="0"
                  allowfullscreen
                ></iframe>
              </div>
            </el-card>
          </div>
        </transition>
      </div>
    </div>

    <el-dialog
      v-model="isNewChatDialogOpen"
      title="✨ 创建新对话"
      width="400px"
      align-center
    >
      <div style="margin-bottom: 12px; color: #606266; font-size: 14px">
        请为本次对话设定一个主题：
      </div>
      <el-input
        v-model="newChatTitle"
        placeholder="例如：Ubuntu 环境配置"
        @keyup.enter="confirmCreateNewChat"
        clearable
        autofocus
      />
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="isNewChatDialogOpen = false">取消</el-button>
          <el-button type="primary" @click="confirmCreateNewChat"
            >确认创建</el-button
          >
        </span>
      </template>
    </el-dialog>

    <el-dialog
      v-model="isRenameDialogOpen"
      title="✏️ 修改对话主题"
      width="400px"
      align-center
    >
      <div style="margin-bottom: 12px; color: #606266; font-size: 14px">
        请输入新的主题名称：
      </div>
      <el-input
        v-model="renameTitle"
        placeholder="请输入新主题"
        @keyup.enter="confirmRenameChat"
        clearable
        autofocus
      />
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="isRenameDialogOpen = false">取消</el-button>
          <el-button type="primary" @click="confirmRenameChat"
            >确认修改</el-button
          >
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import axios from "axios";
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import {
  Fold,
  Expand,
  Plus,
  Cloudy,
  Delete,
  EditPen,
  Position,
  Monitor,
  Close,
} from "@element-plus/icons-vue";
import { ElMessageBox, ElMessage } from "element-plus";

// 👈 1. 引入并实例化咱们的 Pinia 钱包
import { useAuthStore } from "@/stores/auth";
const authStore = useAuthStore();

const API_BASE_URL = "/api";

// 👈 2. 彻底干掉 "user_888"，变成动态响应式的真实用户 ID
const currentUserId = computed(() => authStore.user?.id);

const isHistoryOpen = ref(true);
const currentChatId = ref(null);
const chatHistoryList = ref([]);

const currentChat = computed(() =>
  chatHistoryList.value.find((chat) => chat.id === currentChatId.value),
);

const isNewChatDialogOpen = ref(false);
const newChatTitle = ref("");
const isRenameDialogOpen = ref(false);
const renameTitle = ref("");
const renameTargetId = ref(null);

const isConnected = ref(false);
const isWaiting = ref(false);
const inputText = ref("");
const scrollbar = ref(null);
const socket = ref(null);
const showAndroid = ref(false);
let reconnectTimer = null;
// SeaToken 余额，初始从用户信息读取，通过 WS 实时更新
const seaTokens = ref(0);
// 让 iframe 走 Nginx 的安全代理
const vncUrl = computed(() => {
  // 借用你在 Nginx 里的 /vnc/ 代理通道，autoconnect + password 免去手动输入
  return `${window.location.origin}/vnc/vnc.html?autoconnect=true&password=123456`;
});

const toggleAndroid = () => {
  showAndroid.value = !showAndroid.value;
};

const createNewSession = async (title = "新的对话") => {
  if (!currentUserId.value) return; // 安全锁
  try {
    const res = await axios.post(`${API_BASE_URL}/sessions`, {
      title: title,
      // 🚨 核心修复：强行转换为字符串，迎合 FastAPI 的严格校验！
      user_id: String(currentUserId.value),
    });

    const newSession = res.data;
    newSession.messages = [
      {
        id: "greeting_" + Date.now(),
        role: "ai",
        content: `你好，${
          authStore.user?.username || "主人"
        }！开启了一段关于“${title}”的新对话，请下达指令。`,
        time: new Date().toLocaleTimeString("zh-CN", {
          timeZone: "Asia/Shanghai",
          hour12: false,
        }),
      },
    ];
    chatHistoryList.value.unshift(newSession);
    selectChat(newSession.id);
  } catch (error) {
    console.error("⚠️ 创建对话失败:", error);
  }
};

const fetchAllChats = async () => {
  if (!currentUserId.value) return; // 安全锁
  try {
    const res = await axios.get(
      `${API_BASE_URL}/sessions?user_id=${currentUserId.value}`, // 使用真实 ID
    );
    const sessions = res.data;
    if (sessions && sessions.length > 0) {
      chatHistoryList.value = sessions.map((session) => ({
        ...session,
        messages: [],
      }));
      selectChat(chatHistoryList.value[0].id);
    } else {
      await createNewSession("默认工作区");
    }
  } catch (error) {
    console.error("⚠️ 后端未启动或报错:", error);
  }
};

const selectChat = async (id) => {
  if (currentChatId.value === id && currentChat.value?.messages.length > 0)
    return;
  currentChatId.value = id;
  const chat = currentChat.value;
  if (!chat) return;

  try {
    const res = await axios.get(`${API_BASE_URL}/sessions/${id}/messages`);
    chat.messages = res.data;
    if (chat.messages.length === 0) {
      chat.messages.push({
        id: "greeting_" + Date.now(),
        role: "ai",
        content: `您好！您开启了一段关于“${chat.title}”的新对话，请下达指令。`,
        time: new Date().toLocaleTimeString("zh-CN", {
          timeZone: "Asia/Shanghai",
          hour12: false,
        }),
      });
    }
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || "获取对话失败");
  }
  isWaiting.value = false;
  initWebSocket();
  scrollToBottom();
};

const openNewChatDialog = () => {
  newChatTitle.value = "";
  isNewChatDialogOpen.value = true;
};

const confirmCreateNewChat = async () => {
  const finalTitle = newChatTitle.value.trim() || "新的对话";
  await createNewSession(finalTitle);
  isNewChatDialogOpen.value = false;
};

const openRenameDialog = (id, oldTitle) => {
  renameTargetId.value = id;
  renameTitle.value = oldTitle;
  isRenameDialogOpen.value = true;
};

const confirmRenameChat = async () => {
  const newTitle = renameTitle.value.trim();
  if (!newTitle) return;
  try {
    await axios.put(`${API_BASE_URL}/sessions/${renameTargetId.value}`, {
      title: newTitle,
    });
    const targetChat = chatHistoryList.value.find(
      (chat) => chat.id === renameTargetId.value,
    );
    if (targetChat) targetChat.title = newTitle;
    ElMessage.success("修改主题成功！");
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || "修改主题失败");
  }
  isRenameDialogOpen.value = false;
};

const confirmDeleteChat = (id) => {
  ElMessageBox.confirm(
    "确定要永久删除这个对话记录吗？此操作无法撤销。",
    "🚨 危险操作警告",
    {
      confirmButtonText: "确定删除",
      cancelButtonText: "取消",
      type: "warning",
      confirmButtonClass: "el-button--danger",
    },
  )
    .then(() => {
      executeDeleteChat(id);
    })
    .catch(() => {});
};

const executeDeleteChat = async (id) => {
  try {
    await axios.delete(`${API_BASE_URL}/sessions/${id}`);
  } catch (error) {
    return;
  }
  const index = chatHistoryList.value.findIndex((chat) => chat.id === id);
  if (index !== -1) {
    chatHistoryList.value.splice(index, 1);
    if (currentChatId.value === id) {
      if (chatHistoryList.value.length > 0) {
        selectChat(chatHistoryList.value[0].id);
      } else {
        currentChatId.value = null;
        if (socket.value) socket.value.close();
        await createNewSession("默认工作区");
      }
    }
  }
  ElMessage.success("删除成功");
};

let heartbeatInterval = null;
const initWebSocket = () => {
  if (socket.value) {
    socket.value.onmessage = null;
    socket.value.onopen = null;
    socket.value.onerror = null;
    socket.value.onclose = null;
    socket.value.close();
  }

  // 必须清除上一个定时器，防止定时器叠加导致内存泄漏
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }

  if (!currentChatId.value || !currentUserId.value) return; // 必须有真实ID
  const boundChatId = currentChatId.value;

  // 👈 WebSocket 同样挂载真实的 user_id
  const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const wsUrl = `${wsProtocol}//${window.location.host}/ws/chat/${boundChatId}?user_id=${currentUserId.value}`;
  socket.value = new WebSocket(wsUrl);

  socket.value.onopen = () => {
    isConnected.value = true;
    // 连接成功后，启动心跳！每隔 30 秒给后端发一个心跳包
    heartbeatInterval = setInterval(() => {
      if (socket.value && socket.value.readyState === WebSocket.OPEN) {
        // 确保发送的绝对是一个字符串，绝不要带任何可能引发歧义的符号
        socket.value.send("___PING___");
      }
    }, 25000); // 30000 毫秒 = 30 秒
  };

  socket.value.onmessage = (event) => {
    if (event.data === "___PONG___") return;
    const data = JSON.parse(event.data);
    if (!currentChat.value || currentChatId.value !== boundChatId) return;
    if (data.type === "status" && data.status === "connected") {
      isConnected.value = true;
    } else if (data.type === "tokens_update") {
      seaTokens.value = data.sea_tokens;
      authStore.user && (authStore.user.sea_tokens = data.sea_tokens);
    } else if (data.type === "stream") {
      isWaiting.value = false;
      const msgs = currentChat.value.messages;
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg && lastMsg.role === "ai" && lastMsg.isReceiving) {
        lastMsg.content += data.content;
      }
      scrollToBottom();
    } else if (data.type === "done") {
      const msgs = currentChat.value.messages;
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg && lastMsg.role === "ai") lastMsg.isReceiving = false;
      isWaiting.value = false;
      scrollToBottom();
    } else if (data.type === "error") {
      isConnected.value = false;
      isWaiting.value = false;
      const msgs = currentChat.value.messages;
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg && lastMsg.role === "ai" && lastMsg.isReceiving) {
        lastMsg.content = data.content || "连接异常，请稍后重试";
        lastMsg.isReceiving = false;
      }
      ElMessage.warning((data.content || "OpenClaw 连接异常").replace(/\n|\[|\]|\*/g, ""));
    }
  };
  socket.value.onclose = () => {
    isConnected.value = false;
    isWaiting.value = false;
    // 连接关闭时，一定要清除定时器
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
    }
    if (currentChatId.value === boundChatId) {
      reconnectTimer = setTimeout(() => {
        if (currentChatId.value === boundChatId) initWebSocket();
      }, 1200);
    }
  };
};

// 👈 3. 终极防抖挂载逻辑
onMounted(async () => {
  // 如果是按 F5 刷新了页面，强行先等用户信息拉取完毕，再拉取聊天记录
  if (!authStore.user) {
    await authStore.fetchUserInfo();
  }
  // 初始化 SeaToken 余额
  seaTokens.value = authStore.user?.sea_tokens ?? 0;
  fetchAllChats();
});

onUnmounted(() => {
  if (socket.value) socket.value.close();
  if (heartbeatInterval) clearInterval(heartbeatInterval);
  if (reconnectTimer) clearTimeout(reconnectTimer);
});

const scrollToBottom = async () => {
  await nextTick();
  if (scrollbar.value && scrollbar.value.wrapRef) {
    scrollbar.value.wrapRef.scrollTop = scrollbar.value.wrapRef.scrollHeight;
  }
};

const handleEnter = (e) => {
  if (!e.shiftKey) {
    e.preventDefault();
    sendMessage();
  }
};

const sendMessage = () => {
  const text = inputText.value.trim();
  if (!text || !currentChat.value) return;
  if (!socket.value || socket.value.readyState !== WebSocket.OPEN) {
    initWebSocket();
    ElMessage.warning("连接正在恢复，请稍后再发送");
    return;
  }

  socket.value.send(text);

  currentChat.value.messages.push({
    id: Date.now().toString(),
    role: "user",
    content: text,
    time: new Date().toLocaleTimeString("zh-CN", {
      timeZone: "Asia/Shanghai",
      hour12: false,
    }),
  });
  currentChat.value.messages.push({
    id: (Date.now() + 1).toString(),
    role: "ai",
    content: "",
    time: new Date().toLocaleTimeString("zh-CN", {
      timeZone: "Asia/Shanghai",
      hour12: false,
    }),
    isReceiving: true,
  });
  inputText.value = "";
  isWaiting.value = true;
  scrollToBottom();
};

const clearMessages = () => {
  if (currentChat.value) {
    currentChat.value.messages = [];
  }
};
</script>

<style scoped>
.chat-workspace {
  display: flex;
  height: 100%;
  width: 100%;
  position: relative;
}
.history-sidebar {
  width: 260px;
  background-color: #ffffff;
  border-right: 1px solid #e4e7ed;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}
.history-sidebar.is-collapsed {
  width: 0;
  border-right: none;
}
.history-sidebar-inner {
  width: 260px;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px;
  box-sizing: border-box;
}
.sidebar-top {
  position: relative;
  margin-bottom: 30px;
}
.toggle-btn {
  position: absolute;
  right: 0;
  top: 0;
  cursor: pointer;
  color: #909399;
  font-size: 20px;
  padding: 4px;
  border-radius: 4px;
  transition: 0.2s;
}
.toggle-btn:hover {
  background-color: #f4f4f5;
  color: #303133;
}
.ai-profile {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 10px;
  margin-bottom: 24px;
}
.ai-profile .avatar {
  font-size: 46px;
  background: #f0f7ff;
  border-radius: 50%;
  padding: 8px;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
  margin-bottom: 12px;
}
.ai-profile .name {
  font-weight: bold;
  font-size: 16px;
  color: #303133;
  margin-bottom: 6px;
}
.ai-profile .tag {
  font-size: 12px;
  color: #606266;
  background: #f4f4f5;
  padding: 2px 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.new-chat-btn {
  width: 100%;
  padding: 12px;
  background: #f4f4f5;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  color: #606266;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  transition: 0.2s;
}
.new-chat-btn:hover {
  background: #e9e9eb;
  color: #303133;
  border-color: #dcdfe6;
}
.history-list-container {
  flex: 1;
  overflow-y: auto;
}
.history-list-container::-webkit-scrollbar {
  width: 4px;
}
.history-list-container::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 2px;
}
.list-title {
  font-size: 12px;
  color: #909399;
  margin-bottom: 12px;
  padding-left: 4px;
}
.history-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.history-item {
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  transition: 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.history-item:hover {
  background: #f4f4f5;
}
.history-item.active {
  background: #f0f7ff;
  color: #409eff;
  font-weight: 500;
}
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  padding-right: 8px;
}
.action-icons {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: all 0.2s;
}
.history-item:hover .action-icons {
  opacity: 1;
}
.edit-icon,
.delete-icon {
  font-size: 16px;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}
.edit-icon {
  color: #409eff;
}
.edit-icon:hover {
  background-color: #ecf5ff;
}
.delete-icon {
  color: #f56c6c;
}
.delete-icon:hover {
  background-color: #fde2e2;
}
.expand-btn-container {
  position: absolute;
  left: 15px;
  top: 15px;
  z-index: 10;
}
.chat-main-area {
  flex: 1;
  height: 100%;
  overflow: hidden;
  position: relative;
}
.main-layout {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  height: 100%;
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  overflow: hidden;
}
.chat-section {
  width: 100%;
  max-width: 800px;
  height: 100%;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.chat-section.is-shifted {
  max-width: 600px;
  min-width: 350px;
}
.box-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
}
:deep(.el-card__body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.title {
  font-weight: bold;
  font-size: 16px;
  color: #303133;
}
.android-section {
  height: 100%;
  flex: 1;
  min-width: 600px;
}
.android-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 6px solid #2c3e50;
  border-radius: 12px;
}
.android-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.android-screen-container {
  flex: 1;
  background-color: #000;
  position: relative;
  overflow: hidden;
}
.android-iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  background-color: #1a1a1a;
}
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(30px);
  opacity: 0;
  max-width: 0;
  min-width: 0;
  margin-left: -20px;
}
.message-window {
  flex: 1;
  background-color: #fafafa;
  padding: 20px;
  overflow: hidden;
}
.message-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 20px;
}
.message-wrapper {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.message-wrapper.is-user {
  flex-direction: row-reverse;
}
.avatar {
  font-size: 24px;
  background: #fff;
  border-radius: 50%;
  padding: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.message-bubble {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
.is-user .message-bubble {
  background-color: #ecf5ff;
  color: #409eff;
  border: 1px solid #d9ecff;
  border-top-right-radius: 0;
}
.is-ai .message-bubble {
  background-color: #ffffff;
  color: #303133;
  border: 1px solid #ebeef5;
  border-top-left-radius: 0;
}
.time {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
  text-align: right;
}
.is-user .time {
  color: #a0cfff;
}
.input-area {
  padding: 15px 20px;
  background-color: #fff;
  border-top: 1px solid #ebeef5;
}
.action-bar {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
}
.loading-bubble {
  color: #909399;
  font-weight: bold;
  letter-spacing: 2px;
}
.dot {
  animation: blink 1.4s infinite both;
}
.dot:nth-child(2) {
  animation-delay: 0.2s;
}
.dot:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes blink {
  0% {
    opacity: 0.2;
  }
  20% {
    opacity: 1;
  }
  100% {
    opacity: 0.2;
  }
}
</style>
